import { SEARCH_TYPE } from './../../services/search/search.service';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { combineLatest, Subject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StorageService } from '../../services/storage/storage.service';
import { SearchService, SORTBYTYPES } from './../../services/search/search.service';
import { SharedService } from './../../services/shared/shared.service';
import { AuthService } from '../../services/auth/auth.service';
import { AdvancedSearchService } from './advancedsearch.service';
import { finalize, takeUntil } from 'rxjs/operators';
import {
  FacetIndex,
  FacetMatch,
  SearchRequestPayload,
  TargetGroups,
} from '../../core/interfaces/search.interface';
import { RefineSearchComponent } from './refine-search/refine-search.component';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { SearchHistoryService } from '../../components/side-nav/search-history/search-history.service';

@Component({
  selector: 'ag-advanced-search',
  templateUrl: './advancedsearch.component.html',
  styleUrls: ['./advancedsearch.component.scss'],
  providers: [{ provide: ANIMATION_MODULE_TYPE, useValue: 'BrowserAnimations' }],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AdvancedsearchComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild(RefineSearchComponent) refineSearchComponent!: RefineSearchComponent;
  @Input() topic: boolean;
  @Input() showcase: boolean;
  @Input() grid = true;
  @Input() embeddedComponent: boolean;
  @Input() template: any;
  @Input() topicTemplate: any;
  @Input() chipEdit: any;
  @Input() clear: any;
  @Output() count = new EventEmitter<any>();
  @Output() editedChip = new EventEmitter<any>();

  private _isCollapsed;
  availableFilters: any;
  toggleResource = true;
  toggleAS = true;

  private _hasListenedToParams: boolean;

  resourcesArray: TargetGroups[] = [];
  ind1 = 'all_headings';
  ind2 = 'all_headings';
  ind3 = 'all_headings';
  queryOpt1 = 'ALL';
  queryOpt2 = 'ALL';
  queryOpt3 = 'ALL';
  matchJoinOpt1 = 'AND';
  matchJoinOpt2 = 'AND';
  matchJoinOpt3 = 'AND';
  inputValue1 = '';
  inputValue2 = '';
  inputValue3 = '';
  hitsCount: any;

  sortByValue: string = SORTBYTYPES.RELEVANCE;

  showSaveQueryButton: boolean;
  canDoAdvancedSearch = true;
  showNoResourceMessage = false;

  enableSearch = true;
  advancedSearchIndexes: any;
  resourceLabel: any;
  chipDescription: any;

  // Show spinner onLoad
  fetchingFilters = true;

  private _startRecord: any = 1;
  // topic search chips array
  private _userData: any;
  private _isHistory: any;
  private _searchQuery: any;
  private _filters: any;
  private _isModifySearch: any;
  private _filterLabel: any;
  private _chipPosition: any;
  private _unsubscribeAll: Subject<void> = new Subject<void>();

  constructor(
    public snackBar: MatSnackBar,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _storageService: StorageService,
    private _advancedSearchService: AdvancedSearchService,
    private _searchService: SearchService,
    private _authService: AuthService,
    private _analyticsService: AnalyticsService,
    private _searchHistoryService: SearchHistoryService,
    private _sharedService: SharedService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // -- coming from edit topic search row chip
    if (changes?.topic?.currentValue) {
      this.topic = changes.topic.currentValue;
    }
    if (changes?.topicTemplate?.currentValue) {
      this.topicTemplate = changes.topicTemplate.currentValue;
    }
    if (changes?.chipEdit?.currentValue) {
      this.chipEdit = changes.chipEdit.currentValue;
      this.loadEditTopicInfoAsync();
    }

    // -- coming from edit-c-showcase comp, handle showcase edit component
    if (changes?.template?.currentValue) {
      this.template = changes.template.currentValue;
    }

    if (this.template?.query) {
      this._loadEditShowCaseInfoAsync();
    }
  }

  ngOnInit() {
    this._userData = this._authService.getUserDataFromSession();
    if (!this._userData) {
      throw new Error('userData missing !!!');
    }

    this.enableSearch = this._userData.opacOptions.enableSearch;
    if (!this.template?.query) {
      this.sortByValue =
        this._searchService.sortBy === 'relevance'
          ? this._searchService.sortBy
          : `${this._searchService.sortBy}_${this._searchService.orderBy}`;
    }

    this._activatedRoute.queryParams
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((params: Params) => {
        this._isHistory =
          params['history'] == 'true' ||
          (this.embeddedComponent && this.template && this.template.typeOf != 'edit'); // true if search history or when already created component tried to be edited
        this._isModifySearch = params['fromSearch'] == 'true';

        if (!this._hasListenedToParams) {
          this._hasListenedToParams = true;
        }

        combineLatest([
          this._searchService.getSearchResourcesBS(),
          this._searchService.getSearchIndexesBS(),
        ])
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe(([resources, searchIndexes]) => {
            if (!resources || !searchIndexes) {
              return;
            }

            this.resourcesArray = this._searchService.getSearchResources();
            this.advancedSearchIndexes = searchIndexes.advancedSearchIndexes;

            this._performAdvancedSearchAsync();
            this.fetchFilters();
          });
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  private async _performAdvancedSearchAsync(): Promise<void> {
    this.resetSearch();

    if (this._isHistory || this._isModifySearch) {
      const history = this._searchHistoryService.getHistoryCached();

      if (!history) {
        throw new Error('history info missing !!');
      }

      this._searchQuery = history.searchQuery;
      this._filters = this._searchQuery.filters;

      SearchService.setSelectedSearchResources(
        this.resourcesArray,
        this._searchQuery?.resources ?? [],
      );

      this.sortByValue =
        history.searchQuery.sortBy[0] === 'relevance'
          ? history.searchQuery.sortBy[0]
          : `${history.searchQuery.sortBy[0]}_${history.searchQuery.orderBy}`;

      const facetMatches: FacetMatch[] = this._searchQuery?.facetMatches;
      facetMatches?.forEach((fm) => {
        switch (fm.index) {
          case 'lang':
            this._filters.languages = this.addToFilter(this._filters.languages, fm.query);
            break;
          case 'format_long':
            this._filters.formats = this.addToFilter(this._filters.formats, fm.query);
            break;
          case 'publication_date_range':
          case 'publication_date':
            this._filters.pubYears = this.addToFilter(this._filters.pubYears, fm.query);
            break;
          case 'hld_materialtype':
            this._filters.materialTypes = this.addToFilter(this._filters.materialTypes, fm.query);
            break;
          case 'audience':
            this._filters.forAudiences = this.addToFilter(this._filters.forAudiences, fm.query);
            break;
          case FacetIndex.ACCELERATED_READER_INTEREST_LEVEL:
            this._filters.acceleratedReaderInterestLevel = this.addToFilter(
              this._filters.acceleratedReaderInterestLevel,
              fm.query,
            );
            break;
          case FacetIndex.ACCELERATED_READER_READING_LEVEL + '_range':
          case FacetIndex.ACCELERATED_READER_READING_LEVEL:
            this._filters.acceleratedReaderReadingLevel = this.addToFilter(
              this._filters.acceleratedReaderReadingLevel,
              fm.query,
            );
            break;
          case FacetIndex.ACCELERATED_READER_TITLE_POINT_VALUE + '_range':
          case FacetIndex.ACCELERATED_READER_TITLE_POINT_VALUE:
            this._filters.acceleratedReaderTitlePointValue = this.addToFilter(
              this._filters.acceleratedReaderTitlePointValue,
              fm.query,
            );
            break;
          case FacetIndex.READING_COUNTS_READING_LEVEL + '_range':
          case FacetIndex.READING_COUNTS_READING_LEVEL:
            this._filters.readingCountsReadingLevel = this.addToFilter(
              this._filters.readingCountsReadingLevel,
              fm.query,
            );
            break;
          case FacetIndex.READING_COUNTS_TITLE_POINT_VALUE + '_range':
          case FacetIndex.READING_COUNTS_TITLE_POINT_VALUE:
            this._filters.readingCountsTitlePointValue = this.addToFilter(
              this._filters.readingCountsTitlePointValue,
              fm.query,
            );
            break;
          case FacetIndex.READING_COUNTS_INTEREST_LEVEL:
            this._filters.readingCountsInterestLevel = this.addToFilter(
              this._filters.readingCountsInterestLevel,
              fm.query,
            );
            break;
          case 'has_oclc_number':
            this._filters.hasOclcNumber = this.addToFilter(this._filters.hasOclcNumber, fm.query);
            break;
          case FacetIndex.LEXILE_READING_GRADE_LEVEL + '_range':
          case FacetIndex.LEXILE_READING_GRADE_LEVEL:
            this._filters.Lexile = this.addToFilter(this._filters.Lexile, fm.query);
            break;
          case 'awards':
            this._filters.awards = this.addToFilter(this._filters.awards, fm.query);
            break;
          case 'reading_program':
            this._filters.readingPrograms = this.addToFilter(
              this._filters.readingPrograms,
              fm.query,
            );
            break;
          case 'date_added':
            this._filters.DateItemsAdded = this.addToFilter(this._filters.DateItemsAdded, fm.query);
            break;
          case FacetIndex.CLCD_AGE:
            this._filters.clcdAge = this.addToFilter(this._filters.clcdAge, fm.query);
            break;
          case FacetIndex.CLCD_GRADES:
            this._filters.clcdGrades = this.addToFilter(this._filters.clcdGrades, fm.query);
            break;
        }
      });

      const matches = this._searchQuery?.matches;
      for (let index = 0; index < matches.length; index++) {
        const match = matches[index];
        match.query = match.query?.trim() == '*' ? '' : match.query?.trim();
        if (index == 0) {
          this.inputValue1 = match.query;
          this.queryOpt1 = match.queryOption;
          this.ind1 = match.index;
          this.matchJoinOpt1 = match.matchJoinOperator;
        }
        if (index == 1) {
          this.inputValue2 = match.query;
          this.queryOpt2 = match.queryOption;
          this.ind2 = match.index;
          this.matchJoinOpt2 = match.matchJoinOperator;
        }
        if (index == 2) {
          this.inputValue3 = match.query;
          this.queryOpt3 = match.queryOption;
          this.ind3 = match.index;
          this.matchJoinOpt3 = match.matchJoinOperator;
        }
      }
    }
  }

  private addToFilter(filter, query) {
    filter = filter ?? [];
    filter.push(query);
    return filter;
  }

  async loadEditTopicInfoAsync(): Promise<void> {
    this.template = this.chipEdit; // copy chipEdit query to template query as it is used in getAdvSearchDetails() below
    this._chipPosition = this.chipEdit.position;
    if (this.chipEdit.description) {
      this.chipDescription = Array.isArray(this.chipEdit.description)
        ? this.chipEdit.description[0]
        : this.chipEdit.description;
    }
    if (this.chipEdit.query) {
      this.showSaveQueryButton = true;
      await this._loadEditComponentInfoAsync(this.chipEdit.query);
    }
  }

  private async _loadEditShowCaseInfoAsync(): Promise<void> {
    this._hasListenedToParams = true;
    this.sortByValue = this._searchService.getSortByType(
      this.template.query.searchQuery.sortBy,
      this.template.query.searchQuery.orderBy,
    );
    await this._loadEditComponentInfoAsync(this.template.query);
  }

  private async _loadEditComponentInfoAsync(query): Promise<void> {
    SearchService.setSelectedSearchResources(
      this.resourcesArray,
      query?.searchQuery?.resources ?? [],
    );
    this._searchHistoryService.setHistoryCached(query);
    this._isHistory = true;
    this._isModifySearch = false;
    this.resetSearch();
    this._performAdvancedSearchAsync();
    this.fetchFilters();
  }

  setResources(selectedResources: number[]): void {
    SearchService.setSelectedSearchResources(this.resourcesArray, selectedResources);

    if (selectedResources.length == 0) {
      this.showNoResourceMessage = true;
      this.disableSearch();
      return;
    }

    this.showNoResourceMessage = false;

    //check are there any AG/AV resources
    const shouldFetchFilters = this.resourcesArray
      .flatMap((r) => r.searchTargets)
      .filter((r) => r.isDefault)
      .some((s) => s.targetType == 'AG' || s.targetType == 'AV');

    if (shouldFetchFilters) {
      this.fetchFilters();
    } else {
      const blankFilters = {
        hitsCount: -1,
        filters: [],
        filterLabel: this._filterLabel,
        resourceLabel: this.resourceLabel,
      };
      this._filtersBindings(blankFilters);
    }
  }

  private _filtersBindings(availableFilters) {
    this.availableFilters = availableFilters;
    this.hitsCount = availableFilters.hitsCount;
    this._filterLabel = availableFilters.filterLabel;
    this.resourceLabel = availableFilters.resourceLabel;

    if (this.hitsCount == -1) {
      this.canDoAdvancedSearch = true;
    } else {
      this.canDoAdvancedSearch = this.hitsCount > 0 || this.howNonAGAZSelect();
    }

    this.count.emit(this.hitsCount);
  }

  private disableSearch() {
    this.availableFilters = null;
    this.canDoAdvancedSearch = false;
    this.hitsCount = -1;
    this.count.emit(this.hitsCount);
  }

  private howNonAGAZSelect() {
    return this.resourcesArray
      .map((r) => r.searchTargets.filter((s) => s.isDefault))
      .flat()
      .filter((m) => m)
      .some((s) => s.targetType != 'AG' && s.targetType != 'AV');
  }

  fetchFilters() {
    this.fetchingFilters = true;
    this._advancedSearchService
      .getAvailableFilters(this.buildFilterRequest())
      .pipe(finalize(() => (this.fetchingFilters = false)))
      .subscribe(
        (advancedFilters) => {
          this._filtersBindings(advancedFilters);
        },
        () => {
          this.disableSearch();
        },
      );
  }

  saveChipQuery() {
    if (this.chipDescription == '') {
      this.openSnackBar('Enter description for the chip', 'ok');
    } else {
      const array: any = [];
      array.push(this.chipDescription);

      const emitObj = {
        query: this.buildAdvancedSearchRequest(),
        description: array,
        position: this._chipPosition,
      };

      this.editedChip.emit(emitObj);

      this.showSaveQueryButton = false;
    }
  }

  // ------- empty and reset search boxes ---------------
  resetSearch() {
    this.resourcesArray = this._searchService.getSearchResources();

    this.queryOpt1 = 'ALL';
    this.queryOpt2 = 'ALL';
    this.queryOpt3 = 'ALL';

    this.matchJoinOpt1 = 'AND';
    this.matchJoinOpt2 = 'AND';
    this.matchJoinOpt3 = 'AND';

    this.inputValue1 = '';
    this.inputValue2 = '';
    this.inputValue3 = '';

    this.ind1 = this.advancedSearchIndexes?.advancedSearchBoxOneIndexes.find(
      (x) => x.isDefault == true,
    )?.value;
    this.ind2 = this.advancedSearchIndexes?.advancedSearchBoxTwoIndexes.find(
      (x) => x.isDefault == true,
    )?.value;
    this.ind3 = this.advancedSearchIndexes?.advancedSearchBoxThreeIndexes.find(
      (x) => x.isDefault == true,
    )?.value;
  }

  clearAll() {
    this.refineSearchComponent?.clearAll();
    this.resetSearch();

    const selectedResources = SearchService.getSelectResources(this.resourcesArray);
    this.setResources(selectedResources);
  }

  doAdvancedSearch() {
    if (
      this.hitsCount < 1 &&
      SharedService.isEmpty(this.inputValue1) &&
      SharedService.isEmpty(this.inputValue2) &&
      SharedService.isEmpty(this.inputValue3)
    ) {
      const message = 'Please enter a search term to start searching';
      this.openSnackBar(message, 'ok');
      return;
    }

    this._analyticsService.logEvent('Search', {
      query1: this.inputValue1.trim(),
      index1: this.ind1,
      query2: this.inputValue2.trim(),
      index2: this.ind2,
      query3: this.inputValue3.trim(),
      index3: this.ind3,
    });

    this._storageService.setItem('facetFilterArray', []);
    const resources = SearchService.getSelectResources(this.resourcesArray);
    this._storageService.setItem('history', this.buildAdvancedSearchRequest());

    const matches = this.createMatches();

    let queryString = '';
    if (matches.length > 0) {
      for (let index = 0; index < matches.length; index++) {
        const element = matches[index];
        if (index == 0) {
          queryString = element.query;
          this._storageService.setItem('Search-TERM', queryString);
        } else {
          queryString = queryString + ',' + element.query;
          const q2 = this._storageService.getItem('Search-TERM');
          const dummyStr = q2 + ' ' + element.query;
          this._storageService.setItem('Search-TERM', dummyStr);
        }
      }
      this._storageService.setItem('Search-TERM', queryString);
    } else {
      this._storageService.remove('Search-TERM');
    }

    if (resources.length) {
      this._searchService.doSearch(this.buildAdvancedSearchRequest());
    } else {
      const message = 'Please select resources for search';
      this.openSnackBar(message, 'ok');
    }
  }

  onEnterInput(): void {
    if (!this.canDoAdvancedSearch) {
      return;
    }
    if (!this.embeddedComponent) {
      // if this is a topic search or showcase
      this.doAdvancedSearch();
    }
  }

  buildAdvancedSearchRequest(): SearchRequestPayload {
    const matches = this.createMatches();
    const filters = this.refineSearchComponent?.getFilters() ?? {};
    const sortByOrderBy = this._searchService.getSortAndOrderBy(this.sortByValue);

    return {
      searchQuery: {
        matches: matches,
        filters: filters,
        resources: SearchService.getSelectResources(this.resourcesArray),
        resultType: this._searchService.resultType,
        sortBy: [sortByOrderBy.sortBy],
        orderBy: sortByOrderBy.orderBy,
        startRecord: this._startRecord,
        numOfRecords: this._searchService.numOfRecords,
        searchType: SEARCH_TYPE.ADVANCED,
        resultStatusOnly: false,
        isKidsSearch: this._sharedService.getIsKidsCat(),
      },
    };
  }

  private buildFilterRequest(): object {
    const matches = this.createMatches();
    this._filters = this.refineSearchComponent?.getFilters() ?? this._filters;

    return {
      searchQuery: {
        matches: matches,
        filters: this._filters,
        resources: SearchService.getSelectResources(this.resourcesArray),
      },
    };
  }

  private createMatches() {
    const matches = [];
    if (!SharedService.isEmpty(this.inputValue1)) {
      matches.push(
        this.createMatch(this.ind1, this.inputValue1, this.queryOpt1, this.matchJoinOpt1),
      );
    }

    if (!SharedService.isEmpty(this.inputValue2)) {
      matches.push(
        this.createMatch(this.ind2, this.inputValue2, this.queryOpt2, this.matchJoinOpt2),
      );
    }

    if (!SharedService.isEmpty(this.inputValue3)) {
      matches.push(
        this.createMatch(this.ind3, this.inputValue3, this.queryOpt3, this.matchJoinOpt3),
      );
    }

    return matches;
  }

  private createMatch(index, query, queryOption, matchJoinOperator): object {
    return {
      index: index,
      query: query,
      queryOption: queryOption,
      matchJoinOperator: matchJoinOperator,
    };
  }

  ///////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////// USER EXPERIENCE /////////////////////////////////
  ////////////////////////////////////////////////////////////////////////////////////////////

  toggleGroup() {
    this._isCollapsed = !this._isCollapsed;
  }

  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 5000,
      verticalPosition: 'top',
    });
  }

  collapseResources() {
    this.toggleResource = false;
  }

  expandResources() {
    this.toggleResource = true;
  }

  collapseAS() {
    this.toggleAS = false;
  }

  expandAS() {
    this.toggleAS = true;
  }
}
